import { useQuery } from "@tanstack/react-query";
import { useRef, useState, type FormEvent } from "react";
import { loginExitKey } from "../../auth/LoginPage";
import { AccountHead, AccountSection, goLogin, useAccountUi } from "../../account/accountUi";
import { postJson, readView, useDocumentTitle, useHeadingFocus, useSpaLinks } from "../../config/common";
import { TeamWindow } from "../../config/team";
import { formatInZone } from "../../format/dates";
import { formatText } from "../../i18n/texts";
import { text, type TeamUi } from "../../team/teamText";
import { TeamSwitch, useFeedback } from "../../team/TeamUi";
import { ChatIcon } from "../../ui/icons/Icon";
import { TextField } from "../../users/formParts";
import { UsersError, UsersFrame } from "../../users/usersUi";

// Seguridad (/portal/seguridad): contraseña, verificación en dos pasos, dispositivos de confianza, sesiones abiertas y
// actividad reciente (Seguridad.razor), con el kit. Cambios de diseño, sin quitar funciones:
// - la verificación es un interruptor C09 con la línea de su estado, no dos botones;
// - «Enviar código», «Verificar» y «Confiar en este dispositivo» son un solo paso guiado: se envía el código y al
//   confirmarlo el dispositivo queda de confianza (Identity sigue exigiendo el código verificado);
// - dispositivos y sesiones son filas C17 con su acción; la sesión actual va marcada y cerrar algo pide confirmación C12;
// - cambiar la contraseña avisa antes que cierra todas las sesiones (Identity lo hace) y después lleva a entrar de nuevo.
// En una sesión de soporte todo es de solo lectura (C41) y solo queda la actividad, como en el portal actual.

type Device = { deviceId: number; label: string | null; browser: string | null; os: string | null; expiresAt: string };
type Session = {
  sessionId: string;
  label: string | null;
  browser: string | null;
  os: string | null;
  lastActivityAt: string;
  current: boolean;
};
type Activity = { at: string; key: string; ok: boolean };
type View = {
  isSupport: boolean;
  timeZone: string;
  mfa: string | null;
  devices: Device[];
  sessions: Session[];
  activity: Activity[];
  activityTotal: number;
  activityTake: number;
  activityStep: number;
};
type Reply = {
  saved?: boolean;
  errorKey?: string;
  fields?: Record<string, string>;
  signedOut?: boolean;
  challengeId?: number;
  devOtp?: string | null;
  closed?: number;
};
type Confirm =
  { kind: "device"; device: Device } | { kind: "session"; session: Session } | { kind: "others"; count: number };

/** «Chrome · Windows», o el nombre que Identity le dio; sin datos, «Dispositivo sin identificar». */
const deviceName = (ui: TeamUi, item: { label: string | null; browser: string | null; os: string | null }) =>
  item.label || [item.browser, item.os].filter(Boolean).join(" · ") || text(ui, "ACC_UNKNOWN_DEVICE");

export function SecurityPage({ locale }: { locale: string }) {
  const ui = useAccountUi(locale);
  const [take, setTake] = useState<number | null>(null);
  const view = useQuery({
    queryKey: ["account", "security", take],
    queryFn: () => readView<View>(`/api/portal/account/security${take ? `?take=${take}` : ""}`),
    staleTime: 0,
    placeholderData: (previous) => previous,
  });
  useSpaLinks();
  useHeadingFocus(!!ui && !!view.data, "security");
  useDocumentTitle(ui ? text(ui, "SEC_TITLE") : null);
  if (!ui) return null;
  if (view.data?.status === 401) {
    if (!leaving) goLogin();
    return null;
  }
  const ready = !!view.data || view.isError;
  const data = view.data?.status === 200 ? view.data.body : null;
  const back = { href: "/portal/perfil", labelKey: "NAV_PROFILE" };
  return (
    <UsersFrame ui={ui} ready={ready}>
      <AccountHead ui={ui} title={text(ui, "SEC_TITLE")} sub={text(ui, "ACC_SECURITY_HINT")} back={back} />
      {data ? (
        <SecurityBody
          ui={ui}
          view={data}
          reload={() => void view.refetch()}
          showMore={() => setTake(data.activityTake + data.activityStep)}
          loadingMore={view.isFetching}
          onSignedOut={leave}
        />
      ) : (
        <UsersError
          message={text(ui, "TEAM_ERR_GENERIC")}
          retryLabel={text(ui, "CFG1_RETRY")}
          onRetry={() => void view.refetch()}
        />
      )}
    </UsersFrame>
  );
}

/**
 * Quedar fuera (contraseña cambiada o esta sesión cerrada): el servidor ya cerró la sesión, así que se va al login, que
 * dice qué pasó. El motivo viaja en sessionStorage porque el resto del portal (el chat) también nota la sesión cerrada y
 * puede llegar antes al login: sea quien sea, el aviso se ve igual.
 */
let leaving = false;
function leave(reason: "password" | "session"): void {
  leaving = true;
  try {
    sessionStorage.setItem(loginExitKey, reason);
  } catch {
    // Sin almacenamiento el login abre sin el aviso; la sesión ya está cerrada igual.
  }
  window.location.replace("/login");
}

function SecurityBody({
  ui,
  view,
  reload,
  showMore,
  loadingMore,
  onSignedOut,
}: {
  ui: TeamUi;
  view: View;
  reload: () => void;
  showMore: () => void;
  loadingMore: boolean;
  onSignedOut: (reason: "password" | "session") => void;
}) {
  const T = (key: string) => text(ui, key);
  const feedback = useFeedback();
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const date = (value: string) => formatInZone(value, view.timeZone, ui.locale, "dateTime");

  /** Una acción de la pantalla: toast si salió; si no, el aviso con su motivo. Siempre vuelve a leer. */
  const run = async (url: string, body: unknown, done: (reply: Reply) => void): Promise<boolean> => {
    setError(null);
    try {
      const reply = await postJson<Reply>(url, body);
      if (reply.status === 401) {
        goLogin();
        return false;
      }
      if (reply.status === 200 && reply.body?.saved) {
        done(reply.body);
        return true;
      }
      setError(T(reply.body?.errorKey ?? "CP_ACTION_UNAVAILABLE"));
      return false;
    } catch {
      setError(T("CP_ACTION_UNAVAILABLE"));
      return false;
    } finally {
      reload();
    }
  };

  const confirmAction = async () => {
    if (!confirm || busy) return;
    setBusy(true);
    if (confirm.kind === "device")
      await run(`/api/portal/account/security/devices/${confirm.device.deviceId}/revoke`, {}, () =>
        feedback.show(T("ACC_DEVICE_REVOKED")),
      );
    else if (confirm.kind === "session")
      await run(`/api/portal/account/security/sessions/${confirm.session.sessionId}/revoke`, {}, (reply) =>
        reply.signedOut ? onSignedOut("session") : feedback.show(T("ACC_SESSION_CLOSED")),
      );
    else
      await run("/api/portal/account/security/sessions/revoke-others", {}, (reply) =>
        feedback.show(
          formatText(T(reply.closed === 1 ? "ACC_SESSIONS_CLOSED_ONE" : "ACC_SESSIONS_CLOSED_MANY"), reply.closed ?? 0),
        ),
      );
    setBusy(false);
    setConfirm(null);
  };

  // Un cambio a la vez: un doble clic o «Deshacer» con otro cambio en curso no manda valores cruzados.
  const mfaBusy = useRef(false);
  const setMfa = async (enabled: boolean, done: () => void) => {
    if (mfaBusy.current) return;
    mfaBusy.current = true;
    try {
      await run("/api/portal/account/security/mfa", { enabled }, done);
    } finally {
      mfaBusy.current = false;
    }
  };
  const switchMfa = (enabled: boolean) =>
    setMfa(enabled, () =>
      feedback.show(T(enabled ? "ACC_MFA_ENABLED" : "ACC_MFA_DISABLED"), () => setMfa(!enabled, () => undefined)),
    );

  const others = view.sessions.filter((session) => !session.current).length;
  const mfaOn = view.mfa === "ENABLED";
  const confirmTitle = !confirm
    ? ""
    : confirm.kind === "device"
      ? formatText(T("ACC_DEVICE_REVOKE_Q"), deviceName(ui, confirm.device))
      : confirm.kind === "session"
        ? confirm.session.current
          ? T("ACC_SESSION_CLOSE_CURRENT_Q")
          : formatText(T("ACC_SESSION_CLOSE_Q"), deviceName(ui, confirm.session))
        : T("ACC_SESSIONS_CLOSE_OTHERS_Q");
  const confirmLine = !confirm
    ? ""
    : confirm.kind === "device"
      ? T("ACC_DEVICE_REVOKE_LINE")
      : confirm.kind === "session"
        ? T(confirm.session.current ? "ACC_SESSION_CLOSE_CURRENT_LINE" : "ACC_SESSION_CLOSE_LINE")
        : formatText(
            T(confirm.count === 1 ? "ACC_SESSIONS_CLOSE_OTHERS_LINE_ONE" : "ACC_SESSIONS_CLOSE_OTHERS_LINE_MANY"),
            confirm.count,
          );
  const confirmVerb = !confirm
    ? ""
    : confirm.kind === "device"
      ? T("SEC_REVOKE")
      : confirm.kind === "session"
        ? T("ACC_SESSION_CLOSE")
        : T("SEC_CLOSE_OTHERS");

  return (
    <>
      {view.isSupport ? (
        <div className="tm-notice neutral acc-notice" role="status" data-part="solo-lectura">
          <ChatIcon name="lock-keyhole" />
          <span>{T("SUPPORT_SECURITY_BLOCKED")}</span>
        </div>
      ) : null}
      {error ? <UsersError message={error} /> : null}
      {view.isSupport ? null : (
        <>
          <PasswordSection ui={ui} onSignedOut={() => onSignedOut("password")} />
          <AccountSection id="sec-mfa-title" title={T("SEC_MFA")} part="dos-pasos">
            <TeamSwitch
              title={T("ACC_MFA_SWITCH")}
              line={T(mfaOn ? "ACC_MFA_ON_LINE" : "ACC_MFA_OFF_LINE")}
              checked={mfaOn}
              disabled={view.mfa === null}
              disabledReason={T("TEAM_ERR_GENERIC")}
              onChange={(enabled) => void switchMfa(enabled)}
            />
            {mfaOn ? <TrustDevice ui={ui} onTrusted={reload} /> : null}
          </AccountSection>
          <AccountSection
            id="sec-devices-title"
            title={T("SEC_DEVICES")}
            line={T("ACC_DEVICES_LINE")}
            part="dispositivos"
          >
            {view.devices.length === 0 ? (
              <p className="acc-empty">{T("ACC_DEVICES_EMPTY")}</p>
            ) : (
              <ul className="tm-list acc-list">
                {view.devices.map((device) => (
                  <li className="tm-row" key={device.deviceId} data-part="fila-dispositivo">
                    <div className="tm-row-main">
                      <span className="tm-row-text">
                        <span className="tm-line1">
                          <strong>{deviceName(ui, device)}</strong>
                        </span>
                        <span className="tm-line2">{formatText(T("ACC_DEVICE_UNTIL"), date(device.expiresAt))}</span>
                      </span>
                    </div>
                    <span className="tm-row-end">
                      <button
                        type="button"
                        className="tm-btn secondary acc-row-action"
                        onClick={() => setConfirm({ kind: "device", device })}
                      >
                        {T("SEC_REVOKE")}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </AccountSection>
          <AccountSection
            id="sec-sessions-title"
            title={T("SEC_SESSIONS")}
            part="sesiones"
            action={
              others > 0 ? (
                <button
                  type="button"
                  className="tm-btn secondary"
                  onClick={() => setConfirm({ kind: "others", count: others })}
                >
                  {T("SEC_CLOSE_OTHERS")}
                </button>
              ) : undefined
            }
          >
            {view.sessions.length === 0 ? (
              <p className="acc-empty">{T("SEC_EMPTY")}</p>
            ) : (
              <ul className="tm-list acc-list">
                {view.sessions.map((session) => (
                  <li className="tm-row" key={session.sessionId} data-part="fila-sesion">
                    <div className="tm-row-main">
                      <span className="tm-row-text">
                        <span className="tm-line1">
                          <strong>{deviceName(ui, session)}</strong>
                          {session.current ? <span className="tm-tag accent">{T("CP_SESSION_CURRENT")}</span> : null}
                        </span>
                        <span className="tm-line2">
                          {formatText(T("ACC_SESSION_LAST"), date(session.lastActivityAt))}
                        </span>
                      </span>
                    </div>
                    <span className="tm-row-end">
                      <button
                        type="button"
                        className="tm-btn secondary acc-row-action"
                        onClick={() => setConfirm({ kind: "session", session })}
                      >
                        {T("ACC_SESSION_CLOSE")}
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </AccountSection>
        </>
      )}
      <AccountSection id="sec-activity-title" title={T("SEC_ACTIVITY")} line={T("ACC_ACTIVITY_LINE")} part="actividad">
        {view.activity.length === 0 ? (
          <p className="acc-empty">{T("SEC_EMPTY")}</p>
        ) : (
          <>
            <ul className="tm-list acc-list acc-activity">
              {view.activity.map((item, index) => (
                <li className="tm-row" key={`${item.at}-${index}`} data-part="fila-actividad">
                  <div className="tm-row-main">
                    <span className="tm-row-text">
                      <span className="tm-line1">
                        <strong>{T(item.key)}</strong>
                        {!item.ok ? <span className="tm-tag ochre">{T("ACC_ATTEMPT_FAILED")}</span> : null}
                      </span>
                      <span className="tm-line2">{date(item.at)}</span>
                    </span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="acc-more">
              <span className="tm-list-count">
                {formatText(T("LIST_SHOWING"), view.activity.length, view.activityTotal)}
              </span>
              {view.activityTotal > view.activity.length && view.activityTake < 200 ? (
                <button
                  type="button"
                  className="tm-btn secondary"
                  disabled={loadingMore}
                  aria-busy={loadingMore ? "true" : undefined}
                  onClick={showMore}
                >
                  {T("LIST_SHOW_MORE")}
                </button>
              ) : null}
            </div>
          </>
        )}
      </AccountSection>
      <TeamWindow
        confirm
        title={confirmTitle}
        closeLabel={T("CFG1_CANCEL")}
        open={confirm !== null}
        onDismiss={() => !busy && setConfirm(null)}
        footer={
          <>
            <button type="button" className="tm-btn secondary" onClick={() => setConfirm(null)} disabled={busy}>
              {T("CFG1_CANCEL")}
            </button>
            <button
              type="button"
              className="tm-btn primary"
              disabled={busy}
              aria-busy={busy ? "true" : undefined}
              onClick={() => void confirmAction()}
            >
              {confirmVerb}
            </button>
          </>
        }
      >
        <p className="tm-confirm-line">{confirmLine}</p>
      </TeamWindow>
    </>
  );
}

/** Cambiar la contraseña: la actual y la nueva, con la regla antes de escribir y el aviso de que cierra las sesiones. */
function PasswordSection({ ui, onSignedOut }: { ui: TeamUi; onSignedOut: () => void }) {
  const T = (key: string) => text(ui, key);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [errors, setErrors] = useState<{ current?: string; new?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const form = useRef<HTMLFormElement>(null);
  const focus = (field: "current" | "new") =>
    requestAnimationFrame(() => form.current?.querySelector<HTMLElement>(`[data-field="${field}"] input`)?.focus());

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy) return;
    const found: { current?: string; new?: string } = {};
    if (!current) found.current = T("ACC_PWD_CURRENT_REQUIRED");
    if (next.length < 12 || next.length > 200) found.new = T("CP_PASSWORD_RULE");
    setErrors(found);
    setError(null);
    if (found.current || found.new) return focus(found.current ? "current" : "new");
    setBusy(true);
    try {
      const reply = await postJson<Reply>("/api/portal/account/security/password", {
        currentPassword: current,
        newPassword: next,
      });
      if (reply.status === 401) return goLogin();
      if (reply.status === 200 && reply.body?.saved) {
        setCurrent("");
        setNext("");
        return onSignedOut();
      }
      const fields = reply.body?.fields ?? {};
      if (Object.keys(fields).length > 0) {
        const issues = {
          current: fields.current ? T(fields.current) : undefined,
          new: fields.new ? T(fields.new) : undefined,
        };
        setErrors(issues);
        focus(issues.current ? "current" : "new");
      } else setError(T(reply.body?.errorKey ?? "CP_ACTION_UNAVAILABLE"));
    } catch {
      setError(T("CP_ACTION_UNAVAILABLE"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AccountSection id="sec-password-title" title={T("PWD_CHANGE_TITLE")} line={T("ACC_PWD_LINE")} part="contrasena">
      <form ref={form} className="acc-password" noValidate onSubmit={(event) => void submit(event)}>
        {error ? <UsersError message={error} /> : null}
        <div className="tm-fields">
          <TextField
            id="sec-current"
            field="current"
            label={T("PWD_CURRENT")}
            type="password"
            showLabel={T("ACC_PWD_SHOW")}
            value={current}
            error={errors.current}
            maxLength={200}
            autoComplete="current-password"
            onChange={(value) => {
              setCurrent(value);
              setErrors((old) => ({ ...old, current: undefined }));
            }}
          />
          <TextField
            id="sec-new"
            field="new"
            label={T("PWD_NEW")}
            type="password"
            showLabel={T("ACC_PWD_SHOW")}
            help={T("CP_PASSWORD_RULE")}
            value={next}
            error={errors.new}
            maxLength={200}
            autoComplete="new-password"
            onChange={(value) => {
              setNext(value);
              setErrors((old) => ({ ...old, new: undefined }));
            }}
          />
        </div>
        <div className="acc-inline-actions">
          <button type="submit" className="tm-btn secondary" disabled={busy} aria-busy={busy ? "true" : undefined}>
            {T("PWD_SAVE")}
          </button>
        </div>
      </form>
    </AccountSection>
  );
}

/**
 * Confiar en este equipo: se envía un código y, al confirmarlo, este equipo queda de confianza. El servidor guarda el
 * comprobante en una cookie que la página no ve, y el login ya no pide el código aquí mientras siga vigente. En
 * Development el código se muestra aquí para poder probar sin correo.
 */
function TrustDevice({ ui, onTrusted }: { ui: TeamUi; onTrusted: () => void }) {
  const T = (key: string) => text(ui, key);
  const feedback = useFeedback();
  const [challenge, setChallenge] = useState<{ id: number; devOtp: string | null } | null>(null);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Identity consume el desafío al verificarlo: si después falla «confiar», el reintento no vuelve a verificar.
  const [verifiedId, setVerifiedId] = useState<number | null>(null);

  const send = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const reply = await postJson<Reply>("/api/portal/account/security/mfa/challenge", {});
      if (reply.status === 401) return goLogin();
      if (reply.status === 200 && reply.body?.challengeId) {
        setChallenge({ id: reply.body.challengeId, devOtp: reply.body.devOtp ?? null });
        setOtp("");
        setOtpError(null);
        feedback.show(T("MFA_CHALLENGE_SENT"));
        requestAnimationFrame(() => document.getElementById("sec-otp")?.focus());
      } else setError(T(reply.body?.errorKey ?? "CP_ACTION_UNAVAILABLE"));
    } catch {
      setError(T("CP_ACTION_UNAVAILABLE"));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || !challenge) return;
    const verified = verifiedId === challenge.id;
    if (!verified && !otp.trim()) {
      setOtpError(T("ACC_OTP_REQUIRED"));
      return document.getElementById("sec-otp")?.focus();
    }
    setBusy(true);
    setError(null);
    try {
      if (!verified) {
        const reply = await postJson<Reply>("/api/portal/account/security/mfa/verify", {
          challengeId: challenge.id,
          otp: otp.trim(),
        });
        if (reply.status === 401) return goLogin();
        if (!(reply.status === 200 && reply.body?.saved)) {
          setOtpError(T(reply.body?.fields?.otp ?? reply.body?.errorKey ?? "MFA_ERROR"));
          return document.getElementById("sec-otp")?.focus();
        }
        setVerifiedId(challenge.id);
      }
      const trusted = await postJson<Reply>("/api/portal/account/security/devices/trust", {
        challengeId: challenge.id,
      });
      if (trusted.status === 200 && trusted.body?.saved) {
        setChallenge(null);
        setOtp("");
        feedback.show(T("ACC_DEVICE_TRUSTED"));
        onTrusted();
      } else setError(T(trusted.body?.errorKey ?? "CP_ACTION_UNAVAILABLE"));
    } catch {
      setError(T("CP_ACTION_UNAVAILABLE"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="acc-trust" data-part="confiar-dispositivo">
      <div className="acc-trust-head">
        <span className="acc-trust-text">
          <strong>{T("ACC_TRUST_TITLE")}</strong>
          <small>{T("ACC_TRUST_LINE")}</small>
        </span>
        {challenge ? null : (
          <button
            type="button"
            className="tm-btn secondary"
            disabled={busy}
            aria-busy={busy ? "true" : undefined}
            onClick={() => void send()}
          >
            {T("MFA_SEND_CODE")}
          </button>
        )}
      </div>
      {error ? <UsersError message={error} /> : null}
      {challenge ? (
        <form className="acc-otp" noValidate onSubmit={(event) => void confirm(event)}>
          <div className={`tm-field ${otpError ? "invalid" : ""}`} data-field="otp">
            <label htmlFor="sec-otp">{T("MFA_CODE")}</label>
            <input
              id="sec-otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={12}
              value={otp}
              aria-invalid={otpError ? "true" : undefined}
              aria-describedby={otpError ? "sec-otp-error" : "sec-otp-help"}
              onChange={(event) => {
                setOtp(event.target.value);
                setOtpError(null);
              }}
            />
            {otpError ? (
              <span id="sec-otp-error" className="tm-field-error">
                {otpError}
              </span>
            ) : (
              <small id="sec-otp-help" className="tm-field-help">
                {challenge.devOtp ? `${challenge.devOtp} (${T("DEV_ONLY")})` : T("ACC_OTP_HELP")}
              </small>
            )}
          </div>
          <div className="acc-inline-actions">
            <button type="button" className="tm-btn secondary" disabled={busy} onClick={() => void send()}>
              {T("ACC_OTP_RESEND")}
            </button>
            <button type="submit" className="tm-btn secondary" disabled={busy} aria-busy={busy ? "true" : undefined}>
              {T("ACC_TRUST_CONFIRM")}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}
